/**
 * 机上調査の「調査項目」セレクタ (docs/27 §9-2 (d))。
 *
 * 凡例軸 (地図ピン・ドーナツの色分け) と集計軸 (エリア / 路線の検出件数) を、実効の出力フォーマットが
 * 持つ分類軸から選ぶ。既定は `useStreetSurveyConfig` が解決した値 (地区上書き → テンプレ既定 → 自動)。
 * master は現在の選択を「この地区の既定として保存」できる (`district.aiAnalysis.streetSurveyOverride`、
 * setDoc merge で他の上書きキーを壊さない)。評価列は自動検出結果を表示のみ (設定は M3 / 地区上書き)。
 */
import { Button, useToast } from '@citizen-walk/shared-ui';
import { useState } from 'react';
import { DEFAULT_STREET_SURVEY_KEY, type District } from '@citizen-walk/shared-types';
import { saveStreetSurveySettings } from './streetSurveySurveys';
import type { StreetSurveyConfig } from './useStreetSurveyConfig';

export interface StreetSurveyItemsSelectorProps {
  districtId: string;
  config: StreetSurveyConfig;
  legendAxisKey: string | null;
  aggregateAxisKey: string | null;
  onChangeLegend: (key: string | null) => void;
  onChangeAggregate: (key: string | null) => void;
  /** master のみ「地区の既定として保存」を出す。 */
  canSaveDefault: boolean;
  uid: string | null;
  /** 設定を保存する調査 (docs/98 R-97、未指定 = 既定の調査) と地区 (追加の調査の保存に使う)。 */
  surveyKey?: string;
  district?: District | null;
}

export function StreetSurveyItemsSelector({
  districtId,
  config,
  legendAxisKey,
  aggregateAxisKey,
  onChangeLegend,
  onChangeAggregate,
  canSaveDefault,
  uid,
  surveyKey = DEFAULT_STREET_SURVEY_KEY,
  district = null,
}: StreetSurveyItemsSelectorProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const axes = config.axes;
  if (axes.length === 0) return null;

  const evaluation = config.evaluation;
  const isDefault =
    (legendAxisKey ?? null) === config.defaultLegendAxisKey &&
    (aggregateAxisKey ?? null) === config.defaultAggregateAxisKey;

  async function saveAsDistrictDefault() {
    if (!canSaveDefault || !uid || saving) return;
    setSaving(true);
    try {
      await saveStreetSurveySettings({
        districtId,
        district,
        surveyKey,
        uid,
        patch: {
          override: {
            legendAxisKey: legendAxisKey ?? null,
            aggregateAxisKeys: aggregateAxisKey ? [aggregateAxisKey] : [],
            evaluationField: config.display.evaluationField ?? null,
          },
        },
      });
      toast('success', 'この地区の机上調査の調査項目を保存しました。');
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const selectClass =
    'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

  return (
    <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 items-end">
        <label className="block text-xs text-slate-600 min-w-0">
          色分けする調査項目（凡例）
          <select
            value={legendAxisKey ?? ''}
            onChange={(e) => onChangeLegend(e.target.value || null)}
            className={selectClass}
            style={{ maxWidth: '100%' }}
          >
            {axes.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
                {a.role === 'primary' ? '（主軸）' : ''}
                {evaluation && a.codeField === evaluation.field ? '（評価）' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-slate-600 min-w-0">
          集計する調査項目（エリア・路線の件数）
          <select
            value={aggregateAxisKey ?? ''}
            onChange={(e) => onChangeAggregate(e.target.value || null)}
            className={selectClass}
            style={{ maxWidth: '100%' }}
          >
            {axes.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
                {a.role === 'primary' ? '（主軸）' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="text-xs text-slate-600 min-w-0">
          評価の内訳
          <p className="mt-0.5 rounded border border-slate-200 bg-white px-2 py-1 truncate">
            {evaluation
              ? `${evaluation.label}（${evaluation.polarityLabels.positive} / ${evaluation.polarityLabels.negative} / ${evaluation.polarityLabels.neutral}）`
              : 'なし（評価列が見つかりません）'}
          </p>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span>
          調査項目はこの地区の AI
          解析設定（出力フォーマットと地区の上書き）から自動的に一覧化されます。
          {evaluation
            ? '評価の内訳は検出要素の一覧・エリア/路線の集計に反映されます。'
            : '評価を扱うには、出力フォーマットの評価列またはマスタの「評価の極性」を設定してください。'}
        </span>
        {canSaveDefault && !isDefault && (
          <Button
            size="sm"
            variant="secondary"
            disabled={saving}
            onClick={() => void saveAsDistrictDefault()}
          >
            {saving ? '保存中…' : 'この選択を地区の既定として保存'}
          </Button>
        )}
      </div>
    </div>
  );
}
